/**
 * AssemblySlot — a shared drag/drop seating primitive for the compose-style
 * arcade games.
 *
 * A slot accepts a piece only when the pure model says the seating is legal: the
 * owning game passes `acceptsActive` (computed by its move generator). When the
 * active piece is not composable here, the slot has no legal socket — it shows
 * an inert "no socket" state rather than stamping a rejection on a move that
 * would otherwise have worked. That distinction is the whole lesson: an unsafe
 * composite is unconstructable, not blocked after the fact.
 *
 * Presentation only. The slot emits a `seat` intent on click/drop; the model
 * decides what (if anything) that does.
 *
 * SPDX-License-Identifier: LicenseRef-Proprietary
 * Copyright (c) 2026 Invariant Holdings LLC
 */

import React from "react";

export interface AssemblySlotProps {
  /** Stable label shown in the slot (e.g. the port name). */
  readonly label: string;
  /** The seated piece's label, or null when empty. */
  readonly seated: string | null;
  /**
   * Whether the currently active piece is composable here (from the model's
   * legal-move generator). When false, the slot offers no socket.
   */
  readonly acceptsActive: boolean;
  /** Whether a piece is currently active/held by the player. */
  readonly hasActive: boolean;
  /** Seat the active piece here (a no-op in the model when not legal). */
  readonly onSeat: () => void;
  readonly accent: string;
  readonly muted: string;
  readonly surface: string;
}

/** A single composition socket that only seats a composable piece. */
export function AssemblySlot({
  label,
  seated,
  acceptsActive,
  hasActive,
  onSeat,
  accent,
  muted,
  surface,
}: AssemblySlotProps): React.JSX.Element {
  const open = hasActive && acceptsActive && seated === null;
  const inert = hasActive && !acceptsActive && seated === null;
  const borderColor = seated !== null ? accent : open ? accent : muted;
  const state =
    seated !== null
      ? "seated"
      : open
        ? "open socket"
        : inert
          ? "no socket"
          : "empty";
  return (
    <button
      type="button"
      onClick={onSeat}
      onDragOver={(event) => {
        if (open) event.preventDefault();
      }}
      onDrop={(event) => {
        event.preventDefault();
        if (open) onSeat();
      }}
      disabled={seated !== null || !open}
      aria-label={`${label} slot: ${state}`}
      style={{
        display: "flex",
        flexDirection: "column",
        gap: 4,
        minWidth: 96,
        padding: "10px 12px",
        borderRadius: 12,
        border: `1.5px ${open ? "dashed" : "solid"} ${borderColor}`,
        background: seated !== null ? `${accent}1A` : surface,
        color: seated !== null ? accent : muted,
        cursor: open ? "pointer" : "default",
        opacity: inert ? 0.5 : 1,
        textAlign: "left",
        transition:
          "border-color 160ms ease, background 160ms ease, opacity 160ms ease",
      }}
    >
      <span
        style={{
          fontSize: 10.5,
          fontWeight: 700,
          letterSpacing: "0.04em",
          textTransform: "uppercase",
        }}
      >
        {label}
      </span>
      <span
        style={{
          fontSize: 13,
          fontWeight: 700,
          fontFamily: "ui-monospace, monospace",
        }}
      >
        {seated ?? (open ? "▾ drop here" : inert ? "no socket" : "—")}
      </span>
    </button>
  );
}
